<template>
	<main>
		<section class="w-full bg-white-dark-1 lg:pt-20! md:py-8">
			<Container class="pb-0!">
				<UnavailablePropertyState
					:title="strings.unavailablePropertyTitle"
					:description="strings.unavailablePropertyDescription"
					title-classes="text-primary text-xl md:text-2xl"
					description-classes="text-primary text-sm md:text-lg"
					icon-classes="cn('size-7 text-white')"
					home-url="/"
					:go-to-home-text="strings.goToHome"
					:property-url="propertiesUrl"
					:see-properties-text="seePropertiesText"
					:on-go-to-home-click="handleClickGoToHome"
					:on-view-more-click="handleViewMoreClickOnMessageDisplay"
				/>
			</Container>
		</section>

		<section
			v-if="property && similarProperties && similarProperties.length"
			class="border-b border-white-dark-3 bg-white md:px-20"
		>
			<Container class="pb-0 md:pb-10!">
				<!-- Below fold: hydrate on visible -->
				<LazyVisibleSectionsHighlightedRegionPropertiesDetailsSection
					hydrate-on-visible
					:properties="similarProperties"
					:region="property.region"
					:property-type="property.propertyType.identifier"
					:city="property.city"
					:state="property.state"
					:origin-property-id="property.id"
					:title="`Imóveis semelhantes em ${property.region}`"
					button-text="Ver todos os imóveis semelhantes"
					container-class="px-0! py-8! md:py-2!"
					button-variant="secondary"
					button-class="border-none hover:underline underline-offset-4 decoration-white-dark-3 px-0!"
					carousel-item-class="sm:basis-[32%]"
					carousel-class="w-full gap-0!"
					title-class="text-2xl md:text-[28px]"
					@cta-view-more-clicked="handleViewMoreClickedSimilarProperties"
				/>
			</Container>
		</section>

		<section
			v-if="chosenForYouProperties && chosenForYouProperties.length >= 5"
			class="bg-white md:px-20!"
		>
			<Container class="pt-0 md:pt-8!">
				<!-- Below fold: hydrate on visible -->
				<LazyVisibleSectionsChosenPropertiesSection
					hydrate-on-visible
					:properties="chosenForYouProperties"
					:cta-link="chosenForYouCtaLink"
					class="p-0! md:py-0!"
					:is-loading="isLoadingSelectedProperties"
					container-class="px-0! pt-14! md:pt-10!"
					title-class="text-2xl tracking-wide md:tracking-tight md:text-[28px]"
					@cta_view_more_clicked="handleViewMoreClickedSelectedProperties"
				/>
			</Container>
		</section>
	</main>
</template>

<script setup lang="ts">
import type { Property } from '~/core/entities/property';
import type { SectionName } from '~/types/mixpanel';

import UnavailablePropertyState from '~/components/UnavailableProperty/UnavailablePropertyState.vue';

const messagesData = {
	pt: {
		goToHome: 'Ir para Home',
		seeProperties: 'Ver imóveis em {region}',
		unavailablePropertyTitle: 'Este imóvel não está mais disponível',
		unavailablePropertyDescription:
			'Explore outras opções de imóveis semelhantes na mesma região.'
	}
};

const strings = messagesData.pt;

const props = defineProps<{
	property: Property | null;
	similarProperties: Property[] | null | undefined;
	chosenForYouProperties: Property[] | null | undefined;
	isLoadingSelectedProperties: boolean;
	propertyRegion: string;
	propertiesUrl: string;
	chosenForYouCtaLink: string;
}>();

const emit = defineEmits<{
	(
		e: 'view-more-clicked',
		params: {
			destination: string;
			clickCount: number;
			ctaText: string;
			sectionName: SectionName;
		}
	): void;
	(e: 'go-to-home-clicked'): void;
}>();

const seePropertiesText = computed(() => {
	return strings.seeProperties.replace(
		'{region}',
		String(props.propertyRegion)
	);
});

function handleViewMoreClickedSimilarProperties(event: {
	destination: string;
	clickCount: number;
	ctaText: string;
}) {
	const { destination, clickCount, ctaText } = event;
	emit('view-more-clicked', {
		destination,
		clickCount,
		ctaText,
		sectionName: 'similar properties'
	});
}

function handleViewMoreClickedSelectedProperties(event: {
	href: string;
	clickCount: number;
	ctaText: string;
}) {
	const { href, clickCount, ctaText } = event;
	emit('view-more-clicked', {
		destination: href,
		clickCount,
		ctaText,
		sectionName: 'selected properties'
	});
}

function handleViewMoreClickOnMessageDisplay() {
	emit('view-more-clicked', {
		destination: props.propertiesUrl,
		clickCount: 1,
		ctaText: seePropertiesText.value,
		sectionName: 'property unavailable'
	});
}

function handleClickGoToHome() {
	emit('go-to-home-clicked');
}
</script>
